import { MutableRefObject } from 'react';
import sitecoreIds from 'src/app_config/sitecore-ids.json';

import { Field, Image, Link, RichText, Text } from '@sitecore-jss/sitecore-jss-nextjs';

import { updateSearchQueryString } from 'src/utils/searchContentQueryString';

import Accordion from 'src/components/basicContent/_Accordion/Accordion';
import { Button } from 'src/components/basicContent/_Button/Button';

import ChevronDownIconSvg from 'public/icons/ChevronDown.svg';
import CloseIconSvg from 'public/icons/close.svg';

import { NavigationMenuItems } from '../../NavigationMenu';

import styles from './SubMenu.module.css';

type SubMenuProps = {
	subMenuRef?: MutableRefObject<HTMLDivElement | null>;
	subMenuItems?: NavigationMenuItems;
	submenuPos?: { left: number | string };
	activeSubMenu?: string | null;
	nestedItem?: string | '';
	activeMenuTitle?: Field<string>;
	showTextBlocks?: boolean;
	showNavigationLinks?: boolean;
	showHighlights?: boolean;
	setActiveSubMenu?: (value: null) => void;
};

export default function SubMenu(props: SubMenuProps): JSX.Element {
	return (
		<div className={styles.subMenuContainer} ref={props?.subMenuRef} style={{ left: props?.submenuPos?.left }}>
			<Button title="Close" className={styles.close} onClick={() => props.setActiveSubMenu?.(null)}>
				<CloseIconSvg />
			</Button>

			{/* Text Blocks */}
			{props.showTextBlocks &&
				props.subMenuItems?.items
					?.filter((item) => item.TemplateId === sitecoreIds.Templates.NavigationTextBlockTemplateId)
					.map((item, index) => (
						<div key={`text-block-${index}`} className={styles.textBlock}>
							{item.Title?.value && <h3 className={styles.header}>{item.Title.value}</h3>}
							<RichText field={item.Text} className={styles.richText} />
						</div>
					))}

			{/* Navigation Links */}
			{props.showNavigationLinks &&
				props.subMenuItems?.items
					?.filter((item) => item.TemplateId === sitecoreIds.Templates.NavigationLinksTemplateId)
					.map((item, index) => {
						return (
							<div key={`nav-links-${index}`} className={`${styles.pageLinks} ${styles.pageLinksMobile}`}>
								{item.Title?.value && <h3 className={styles.header}>{item.Title.value}</h3>}
								{item.items?.map((linkItem, linkIndex) => {
									const linkQuery = linkItem.Link?.value?.querystring;
									const linkText = linkItem.Link?.value?.text?.toLocaleLowerCase();
									const nestedItem = props.nestedItem;

									const isActiveLink = nestedItem && (nestedItem === linkQuery || nestedItem?.toLocaleLowerCase() === linkText);

									const isActiveTitle = linkItem.items?.find((item) => item.Link?.value?.href === nestedItem);

									return (
										<div key={`secondary-link-wrapper-${linkIndex}`} className={styles.pageLinksWrapper}>
											{linkItem.Link ? (
												<ul className={styles.list}>
													<li>
														<Link
															field={linkItem.Link}
															className={`link linkOffset linkHoverWeighted ${isActiveLink ? styles.active : ''}`}
															onClick={() => props.setActiveSubMenu?.(null)}
														/>
													</li>
												</ul>
											) : (
												<Accordion
													sectionClass={styles.accordion}
													headerClass={`${styles.accordionHeader} ${styles.btn} ${isActiveTitle ? styles.active : ''}`}
													bodyClass={styles.accordionBody}
												>
													<>
														<span className={styles.accordionChevron}>
															<ChevronDownIconSvg className={styles.chevron} />
														</span>
														<Text
															tag="span"
															field={linkItem.Title}
															className={`link linkOffset linkHoverWeighted ${isActiveTitle ? styles.active : ''}`}
														/>
													</>
													<div className={styles.accordionBodyContent}>
														{linkItem.items?.map((item) => {
															const className = `${styles.link} link linkOffset linkHoverWeighted linkHoverSecondary ${
																item.Link?.value?.href === props.nestedItem ? styles.active : ''
															}`;

															return item.Link.value.linktype === 'internal' && item.Link.value.querystring ? (
																<a
																	key={item.Title?.value}
																	href={updateSearchQueryString(item.Link.value.href ?? '', item.Link.value.querystring || '')}
																	className={className}
																	onClick={() => props.setActiveSubMenu?.(null)}
																>
																	{item.Title?.value}
																</a>
															) : (
																<Link
																	key={item.Title?.value}
																	field={item.Link}
																	className={className}
																	onClick={() => props.setActiveSubMenu?.(null)}
																/>
															);
														})}
													</div>
												</Accordion>
											)}
										</div>
									);
								})}
							</div>
						);
					})}

			{/* Highlights */}
			{props.showHighlights &&
				props.subMenuItems?.items
					?.filter((item) => item.TemplateId === sitecoreIds.Templates.NavigationHighlightTemplateId)
					.map((item, index) => (
						<div key={`highlight-${index}`} className={styles.highLights}>
							{item.Title?.value && <h3 className={styles.header}>{item.Title.value}</h3>}
							{(item.Image.value?.src !== '' || item.Link.value.href?.trim() !== '') && (
								<div className={styles.imagePillWrapper}>
									{item.Image.value?.src && item.Link.value.href?.trim() !== '' ? (
										<>
											<Link field={item.Link} onClick={() => props.setActiveSubMenu?.(null)}>
												<Image className={styles.image} field={item.Image} />
											</Link>
											<Text tag="span" field={item.PillText} className={styles.pill} />
										</>
									) : item.Image.value?.src ? (
										<>
											<Image className={styles.image} field={item.Image} />
											<Text tag="span" field={item.PillText} className={styles.pill} />
										</>
									) : item.Link.value.href !== '' ? (
										<Link field={item.Link} className={styles.highlightLink} onClick={() => props.setActiveSubMenu?.(null)} />
									) : null}
								</div>
							)}
							<RichText field={item.Text} />
						</div>
					))}
		</div>
	);
}
